import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useAuthState, hasClerkConfig } from "../auth/AuthProvider";
import { createApiStorageAdapter } from "./apiStorageAdapter";
import { getStorageModeOverride } from "./storageMode";
import {
  setStoragePreference,
} from "./storagePreference";

function toTimestamp(value) {
  const n = Number(value);
  if (Number.isFinite(n) && n > 0) return n;
  if (typeof value === "string" && value.trim()) {
    const parsed = Date.parse(value);
    if (Number.isFinite(parsed) && parsed > 0) return parsed;
  }
  return 0;
}

function snapshotUpdatedAt(result) {
  const data = result?.data || null;
  const projTs = toTimestamp(data?.meta?.updatedAt ?? data?.updatedAt ?? 0);
  const init = Array.isArray(data?.initiatives?.allIds)
    ? data.initiatives.allIds
        .map((id) => data?.initiatives?.byId?.[id])
        .filter(Boolean)
        .sort((a, b) => toTimestamp(b?.updatedAt || 0) - toTimestamp(a?.updatedAt || 0))[0]
    : null;
  const initTs = toTimestamp(init?.updatedAt ?? 0);
  return Math.max(projTs, initTs, 0);
}

function normalizeProjectsListResponse(value) {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.projects)) return value.projects;
  return [];
}

export function useStorageManager() {
  const { isLoaded, isSignedIn, userId, getToken } = useAuthState();
  const [forceLocal] = useState(false);
  const [preferredMode, setPreferredModeState] = useState("cloud");
  const [lastError, setLastError] = useState(null);
  const inFlightProjects = useRef(new Map());
  const inFlightProjectList = useRef(null);

  const apiAdapter = useMemo(
    () => createApiStorageAdapter({ getToken }),
    [getToken]
  );

  const overrideMode = getStorageModeOverride();
  const cloudConfigured = hasClerkConfig && overrideMode !== "local";
  const cloudAllowed = cloudConfigured;

  useEffect(() => {
    if (!userId || !isSignedIn || !cloudAllowed) return;
    setStoragePreference(userId, "cloud");
    setPreferredModeState("cloud");
  }, [userId, isSignedIn, cloudAllowed]);
  const shouldUseCloud = cloudAllowed;

  const mode = "cloud";
  const adapter = apiAdapter;

  const setPreferredMode = useCallback(
    () => {
      if (!userId) return;
      setStoragePreference(userId, "cloud");
      setPreferredModeState("cloud");
    },
    [userId]
  );

  const handleError = useCallback(
    (err) => {
      setLastError(err);
    },
    []
  );

  const withFallback = useCallback(
    async (fn) => {
      try {
        setLastError(null);
        return await fn();
      } catch (err) {
        handleError(err);
        throw err;
      }
    },
    [handleError]
  );

  const loadProjects = useCallback(async () => {
    if (inFlightProjectList.current) return inFlightProjectList.current;
    const task = (async () => {
      try {
        const run = async () => {
          const res = await apiAdapter.loadProjects();
          return normalizeProjectsListResponse(res);
        };
        const timeoutMs = 20000;
        const result = await Promise.race([
          run(),
          new Promise((_, reject) =>
            setTimeout(() => reject(new Error("Load timeout")), timeoutMs)
          ),
        ]);
        return result;
      } finally {
        inFlightProjectList.current = null;
      }
    })();
    inFlightProjectList.current = task;
    return task;
  }, [apiAdapter]);

  const loadProject = useCallback(
    async (projectId) => {
      if (!projectId) return { data: null, version: 1 };
      const existing = inFlightProjects.current.get(projectId);
      if (existing) return existing;

      const task = (async () => {
        try {
          return await apiAdapter.loadProject(projectId);
        } finally {
          inFlightProjects.current.delete(projectId);
        }
      })();

      inFlightProjects.current.set(projectId, task);
      return task;
    },
    [apiAdapter]
  );

  const saveProject = useCallback(
    (projectId, snapshot, options) =>
      apiAdapter.saveProject(projectId, snapshot, options),
    [apiAdapter]
  );

  const deleteProject = useCallback(
    (projectId) => apiAdapter.deleteProject(projectId),
    [apiAdapter]
  );

  const createProject = useCallback(
    (name, options) => apiAdapter.createProject(name, options),
    [apiAdapter]
  );

  const cloudAvailable = cloudAllowed && isLoaded && isSignedIn;

  const inspectSources = useCallback(async () => {
    const localProjects = [];
    let cloudProjects = [];
    if (cloudAvailable) {
      try {
        cloudProjects = await apiAdapter.loadProjects();
      } catch (err) {
        handleError(err);
      }
    }
    return {
      localProjects,
      cloudProjects,
      localCount: 0,
      cloudCount: cloudProjects.length,
    };
  }, [apiAdapter, cloudAvailable, handleError]);

  const clearError = useCallback(() => setLastError(null), []);

  return useMemo(
    () => ({
      mode,
      adapter,
      cloudAvailable,
      inspectSources,
      preferredMode,
      setPreferredMode,
      loadProjects,
      loadProject,
      saveProject,
      deleteProject,
      createProject,
      lastError,
      clearError,
      forceLocal,
      isAuthReady: isLoaded,
    }),
    [
      mode,
      adapter,
      cloudAvailable,
      inspectSources,
      preferredMode,
      setPreferredMode,
      loadProjects,
      loadProject,
      saveProject,
      deleteProject,
      createProject,
      lastError,
      clearError,
      forceLocal,
      isLoaded,
    ]
  );
}
